import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * The single rule for "does this value mean on?".
 *
 * Plain JavaScript truthiness, which is what the native
 * `CheckboxControlValueAccessor` this component replaced produced: it assigned
 * straight to the DOM `checked` property, so `''`, `0`, `null` and `undefined`
 * all rendered off. A store setting that has never been saved arrives as `''`
 * (see e.g. `AUTO_ACCEPT_ORDERS: ['']` in store-order-management), and it must
 * read as off.
 *
 * The one addition is the string `'false'`. Several forms (the offer
 * "sellable" control among them) persist 'true'/'false' as strings, and a
 * non-empty string is truthy, so that case has to be named explicitly.
 */
export function isOn(value: unknown): boolean {
  return !!value && `${value}` !== 'false';
}

/**
 * Reusable on/off toggle. Renders the shared `.switch .slider.round` markup
 * (see src/scss/_inputs.scss) with a single, consistent (secondary-brand)
 * colour — replacing the three inconsistent toggle forms in the admin
 * (custom `.switch`, themed `mat-slide-toggle`, and bare teal Material).
 *
 * Optional `offLabel` / `onLabel` render on either side of the switch.
 * Works with `formControlName`, `[formControl]` and `[(ngModel)]`.
 */
@Component({
  selector: 'app-toggle',
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true,
    },
  ],
})
export class ToggleComponent implements ControlValueAccessor {
  @Input() onLabel = '';
  @Input() offLabel = '';
  @Input() helpText = '';
  /** Optional tooltip shown as a title on a `?` marker beside the labels. */
  @Input() tooltip = '';
  @Input() disabled = false;
  /** Optional id applied to the checkbox input (e2e / label targeting). */
  @Input() inputId = '';
  /** Optional data-testid applied to the slider for e2e targeting. */
  @Input() testId: string | null = null;
  /** Optional value attribute on the input (handlers reading $event.target.value). */
  @Input() nativeValue: string | null = null;
  /** Standalone (non-form) usage: drive the checked state from the parent. */
  @Input() set checked(v: boolean | null | undefined) {
    if (v !== null && v !== undefined) {
      this.value = isOn(v);
    }
  }

  /** Standalone usage: the raw change event (target.checked / target.value). */
  @Output() changeEvent = new EventEmitter<Event>();

  value = false;

  private onChange: (value: boolean) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = isOn(value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onNativeChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.value = checked;
    this.onChange(checked);
    this.onTouched();
    this.changeEvent.emit(event);
  }
}
